import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import {ANALYTICS_MODULE, ActionOrigin, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  DatasetViewQuery,
  DatasetViewQueryVariables,
} from 'data-frames/workspace/__generated/DatasetViewQuery';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import DatasetDetails from './DatasetDetails';

interface DatasetViewProps {
  id: string;
}

export const DATASET_VIEW_QUERY: DocumentNode = gql`
  query DatasetViewQuery($id: StrictString!) @skipAudit @engComponent(name: DATA_FRAMES) {
    dataset(id: $id) {
      ... on DatasetNode {
        id
        name
        createdAt
        modifiedAt
        displayId
        originType
        lineageParent {
          ... on ConnectionNode {
            id
          }
          ... on AnalysisNode {
            id
          }
          ... on StudyNode {
            id
          }
          ... on RunNode {
            id
            apiId
          }
          ... on AutomationOutputFileNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        creator {
          id
          name
          handle
          avatarUrl
        }
        folder {
          id
        }
        permissions {
          readable
          writable
          archivable
          unarchivable
        }
        customFields {
          name
          value
        }
        dataFrame {
          ...InaccessibleInfoGraphQL
          ... on DataFrameNode {
            id
            size
          }
        }
        ...CoreObjectArchivableGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('CoreObjectArchivable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function DatasetView({id}: DatasetViewProps): JSX.Element | null {
  assert(
    BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS,
    'ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS is not enabled'
  );
  const result = useBenchlingQuery<DatasetViewQuery, DatasetViewQueryVariables>(DATASET_VIEW_QUERY, {
    variables: {id},
  });

  useOnMount(() => {
    Analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.VIEWED_WORKSPACE_ITEM,
      ANALYTICS_MODULE.INSIGHTS,
      {
        event_group_id: Analytics.makeAnalyticsEventId(),
        action_origin: ActionOrigin.UI_UNDETERMINED,
        viewed_object_id: id,
        viewed_object_type: 'dataset',
      }
    );
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.dataset)) {
      return (
        <WorkspaceItemInaccessibleGuard
          inaccessibleInfo={data.dataset}
          modelType={ModelTypeConstants.DATASET}
        />
      );
    } else {
      const reload = (): void => {
        result.refetch({id});
      };
      return <DatasetDetails dataset={data.dataset} reload={reload} />;
    }
  } else {
    return null;
  }
}
